import { useCallback, useRef, useState } from 'react';
import { Building2, LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { baseURL } from '../../AxiosConfig';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MenuTooltip from './MenuTooltip';
import MenuPopover from './MenuPopover';

export const Conta = ({ expanded, showTooltip }: { expanded?: boolean; showTooltip?: boolean }) => {
    const { shortMenu } = useAppContext();
    const isExpanded = expanded ?? !shortMenu;
    const { user } = useAuthContext();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);
    const userLabel = user?.displayName || user?.email || 'Conta';
    const itemClassName = [
        'sf-menu-item flex min-h-10 w-full items-center gap-3 rounded-lg text-sm font-medium',
        isExpanded ? 'min-w-0 px-3 py-2' : 'justify-center px-0',
        open ? 'is-open' : '',
    ].join(' ');

    return (
        <>
            <MenuTooltip label={userLabel} enabled={(showTooltip ?? shortMenu) && !open}>
                <li>
                    <button
                        ref={triggerRef}
                        type="button"
                        className={itemClassName}
                        onClick={() => setOpen(!open)}
                        aria-label={!isExpanded ? userLabel : undefined}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                    >
                        <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-neutral">
                            <img
                                src={`${baseURL}/users/profileImage/${user?.uid}`}
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {isExpanded && <span className="truncate">{userLabel}</span>}
                    </button>
                </li>
            </MenuTooltip>
            <MenuPopover open={open} onClose={close} anchorRef={triggerRef} label="Conta" width={288}>
                <ContaPanel onClose={close} />
            </MenuPopover>
        </>
    );
};

const ContaPanel = ({ onClose }: { onClose: () => void }) => {
    const navigate = useNavigate();
    const { logout, user } = useAuthContext();
    const { theme, toggleTheme } = useAppContext();
    const displayName = user?.displayName || user?.email || 'Conta';
    const email = user?.email || '';
    const isDark = theme === 'dark';
    const rowClassName = 'sf-menu-item flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] font-medium';

    return (
        <div className="p-2">
            <div className="flex items-center gap-3 px-2 pb-3 pt-2">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-neutral">
                    <img
                        src={`${baseURL}/users/profileImage/${user?.uid}`}
                        alt=""
                        className="h-full w-full object-cover"
                    />
                </div>
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-base-content">{displayName}</div>
                    {email && <div className="truncate text-xs text-base-content/55">{email}</div>}
                </div>
            </div>

            <div className="sf-popover-separator" />

            <ul className="menu gap-0.5 p-0 pt-1">
                <li>
                    <button
                        type="button"
                        className={rowClassName}
                        onClick={() => {
                            navigate('/configuracoes/usuario');
                            onClose();
                        }}
                    >
                        <UserRound size={16} strokeWidth={1.75} className="shrink-0 text-base-content/55" aria-hidden="true" />
                        Minha conta
                    </button>
                </li>
                {(user?.root || user?.admin) && (
                    <li>
                        <button
                            type="button"
                            className={rowClassName}
                            onClick={() => {
                                navigate('/configuracoes/estabelecimento');
                                onClose();
                            }}
                        >
                            <Building2 size={16} strokeWidth={1.75} className="shrink-0 text-base-content/55" aria-hidden="true" />
                            Estabelecimento
                        </button>
                    </li>
                )}
                <li>
                    <button
                        type="button"
                        className={rowClassName}
                        onClick={toggleTheme}
                        aria-pressed={isDark}
                    >
                        {isDark ? <Sun size={16} strokeWidth={1.75} className="shrink-0 text-base-content/55" aria-hidden="true" /> : <Moon size={16} strokeWidth={1.75} className="shrink-0 text-base-content/55" aria-hidden="true" />}
                        {isDark ? 'Modo claro' : 'Modo escuro'}
                    </button>
                </li>
            </ul>

            <div className="sf-popover-separator my-1" />

            <ul className="menu gap-0.5 p-0">
                <li>
                    <button
                        type="button"
                        className={`${rowClassName} text-error`}
                        onClick={() => {
                            onClose();
                            logout();
                        }}
                    >
                        <LogOut size={16} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
                        Sair
                    </button>
                </li>
            </ul>
        </div>
    );
};
